import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import AppHeader from '../components/AppHeader';
import AppFooter from '../components/AppFooter';
import AnimatedGradientBackground from '../components/AnimatedGradientBackground';
import {
  SparklesIcon,
  UsersIcon,
  Squares2X2Icon,
  RocketLaunchIcon,
  LightBulbIcon,
  ChartBarIcon,
  CursorArrowRaysIcon,
  CloudArrowUpIcon,
  ShieldCheckIcon,
  ClockIcon,
  CheckCircleIcon,
  PlayIcon,
  PauseIcon,
  ArrowRightIcon,
  StarIcon,
  HeartIcon,
  BoltIcon,
  GlobeAltIcon,
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import TypingIndicator from '../components/TypingIndicator';
import MoodBoardDemo from '../components/MoodBoardDemo';
import KanbanBoardDemo from '../components/KanbanBoardDemo';
import DesignSprintDemo from '../components/DesignSprintDemo';

const features = [
  {
    title: 'Real-time Collaboration',
    description: 'Watch ideas come to life together with live cursors and instant updates.',
    detailedDescription: 'See your team members\' cursors in real-time as they work. No more confusion about who\'s working on what. Every change is instantly visible to everyone.',
    Icon: UsersIcon,
    color: 'from-blue-500 to-cyan-500'
  },
  {
    title: 'Multiple Board Types',
    description: 'Mood boards, brainstorming, Kanban and full design sprints – all in one place.',
    detailedDescription: 'From initial brainstorming to final presentation, we have the perfect board for every stage of your creative process.',
    Icon: Squares2X2Icon,
    color: 'from-purple-500 to-pink-500'
  },
  {
    title: 'Professional Templates',
    description: 'Jump-start every project with beautifully crafted templates.',
    detailedDescription: 'Save hours of setup time with our curated collection of professional templates designed by industry experts.',
    Icon: SparklesIcon,
    color: 'from-yellow-500 to-orange-500'
  },
  {
    title: 'Cloud Powered',
    description: 'No installs. Access anywhere, any time – securely in the cloud.',
    detailedDescription: 'Your work is automatically saved and synced across all devices. Never lose your progress again.',
    Icon: CloudArrowUpIcon,
    color: 'from-green-500 to-emerald-500'
  }
];

const pricingPlans = [
  {
    name: 'Free',
    price: '$0',
    period: 'forever',
    description: 'Best for trying StyleSync solo',
    features: [
      'Up to 3 personal mood boards',
      'Up to 3 teams',
      'Basic templates',
      'Real-time collaboration',
      'Cloud storage (1GB)',
      'Community support'
    ],
    popular: false,
    buttonText: 'Get Started Free',
    buttonVariant: 'outline'
  },
  {
    name: 'Pro',
    price: 'CAD $12',
    period: 'per month',
    description: 'Individual Pro for power users',
    features: [
      'Unlimited personal mood boards',
      'Create and join unlimited teams',
      'Export/download PNG & PDF',
      'Premium templates',
      'Cloud storage (10GB)',
      'Priority support'
    ],
    popular: true,
    buttonText: 'Upgrade to Pro',
    buttonVariant: 'primary'
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    period: 'contact us',
    description: 'For organizations with advanced needs',
    features: [
      'Everything in Pro',
      'Unlimited storage',
      'SSO integration',
      'Advanced security',
      'Dedicated support',
      'Custom integrations',
      'Team management',
      'API access'
    ],
    popular: false,
    buttonText: 'Contact Sales',
    buttonVariant: 'outline'
  }
];

const testimonials = [
  {
    name: 'Sarah Chen',
    role: 'Design Lead at TechCorp',
    content: 'StyleSync transformed how our design team collaborates. The real-time features are game-changing!',
    avatarUrl:
      'https://images.unsplash.com/photo-1544005313-94ddf0286df2?q=80&w=256&auto=format&fit=crop&crop=faces',
    rating: 5
  },
  {
    name: 'Marcus Rodriguez',
    role: 'Product Manager',
    content: 'From brainstorming to final presentation, everything flows seamlessly. Highly recommended!',
    avatarUrl:
      'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?q=80&w=256&auto=format&fit=crop&crop=faces',
    rating: 5
  },
  {
    name: 'Emily Watson',
    role: 'Creative Director',
    content: 'The templates saved us hours of setup time. Our productivity increased by 40%!',
    avatarUrl:
      'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?q=80&w=256&auto=format&fit=crop&crop=faces',
    rating: 5
  }
];

const howItWorks = [
  {
    step: '01',
    title: 'Create Your Workspace',
    description: 'Sign up and create your first project workspace in seconds',
    icon: RocketLaunchIcon
  },
  {
    step: '02',
    title: 'Choose Your Board Type',
    description: 'Select from brainstorming, Kanban, mood boards, or design sprints',
    icon: Squares2X2Icon
  },
  {
    step: '03',
    title: 'Invite Your Team',
    description: 'Add team members and start collaborating in real-time',
    icon: UsersIcon
  },
  {
    step: '04',
    title: 'Bring Ideas to Life',
    description: 'Use our powerful tools to visualize and organize your creative process',
    icon: SparklesIcon
  }
];

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [currentFeature, setCurrentFeature] = useState(0);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [showVideoModal, setShowVideoModal] = useState(false);

  // Auto-rotate features
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentFeature((prev) => (prev + 1) % features.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleVideoDemo = () => {
    setShowVideoModal(true);
    setIsVideoPlaying(true);
  };

  const closeVideoModal = () => {
    setShowVideoModal(false);
    setIsVideoPlaying(false);
  };

  return (
    <div className="relative min-h-screen flex flex-col overflow-hidden">
      <AnimatedGradientBackground />
      {/* Header */}
      <AppHeader showAuthSection={false} />

      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-purple-300 via-blue-300 to-transparent blur-3xl opacity-50"
          initial={{ scale: 0.8, rotate: 0 }}
          animate={{ scale: 1.2, rotate: 360 }}
          transition={{ duration: 30, repeat: Infinity, repeatType: 'reverse' }}
        />
        <motion.div
          className="absolute -bottom-32 -left-32 w-[400px] h-[400px] rounded-full bg-gradient-to-br from-blue-300 via-purple-300 to-transparent blur-3xl opacity-40"
          initial={{ scale: 1.2, rotate: 0 }}
          animate={{ scale: 0.8, rotate: -360 }}
          transition={{ duration: 25, repeat: Infinity, repeatType: 'reverse' }}
        />
        <motion.div
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-gradient-to-br from-pink-200 via-purple-200 to-transparent blur-3xl opacity-30"
          initial={{ scale: 0.5, rotate: 0 }}
          animate={{ scale: 1.5, rotate: 180 }}
          transition={{ duration: 40, repeat: Infinity, repeatType: 'reverse' }}
        />
      </div>

      {/* Hero Section with Video Background */}
      <section className="hero-section relative flex-grow flex flex-col items-center justify-center text-center px-6 pt-12 pb-24 min-h-screen">
        {/* Soft vignette overlay to improve text contrast */}
        <div className="hero-vignette" />
        {/* Video Background */}
        <div className="absolute inset-0 z-0">
          <div className="relative w-full h-full bg-gradient-to-br from-blue-900/20 to-purple-900/20">
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-blue-600/10 to-purple-600/10"
              animate={{
                background: [
                  'linear-gradient(45deg, rgba(59, 130, 246, 0.1), rgba(147, 51, 234, 0.1))',
                  'linear-gradient(45deg, rgba(147, 51, 234, 0.1), rgba(59, 130, 246, 0.1))',
                  'linear-gradient(45deg, rgba(59, 130, 246, 0.1), rgba(147, 51, 234, 0.1))'
                ]
              }}
              transition={{ duration: 8, repeat: Infinity }}
            />
          </div>
        </div>

        <div className="hero-content relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-6"
          >
            <span className="inline-flex items-center px-4 py-2 rounded-full bg-blue-100 text-blue-800 text-sm font-medium">
              <SparklesIcon className="w-4 h-4 mr-2" />
              Trusted by 10,000+ creative teams
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="hero-title text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-tight mb-8 max-w-6xl"
          >
            Where Creative Teams Meet Visually
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="hero-subtitle mt-6 text-2xl sm:text-3xl max-w-4xl mx-auto leading-relaxed"
          >
            StyleSync unifies brainstorming, organizing, and presenting ideas – all in one collaborative workspace. 
            <span className="font-semibold text-white"> No more scattered tools, no more missed deadlines.</span>
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            className="mt-12 flex justify-center"
          >
            <button
              onClick={() => navigate('/auth')}
              className="btn-shine px-10 py-5 rounded-xl bg-gradient-to-r from-indigo-500 via-blue-600 to-purple-600 text-white font-semibold text-lg shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02]"
            >
              Get Started Free
              <ArrowRightIcon className="w-5 h-5 ml-2 inline" />
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8, duration: 0.8 }}
            className="mt-10 flex flex-wrap items-center justify-center gap-3 sm:gap-4 text-sm"
          >
            <div className="flex items-center gap-2 px-3.5 py-2 rounded-full bg-white/10 border border-white/15 text-white/95 backdrop-blur-sm shadow-sm">
              <CheckCircleIcon className="w-4 h-4 text-emerald-400" />
              <span>No credit card required</span>
            </div>
            <div className="flex items-center gap-2 px-3.5 py-2 rounded-full bg-white/10 border border-white/15 text-white/95 backdrop-blur-sm shadow-sm">
              <CheckCircleIcon className="w-4 h-4 text-emerald-400" />
              <span>Free forever plan</span>
            </div>
            <div className="flex items-center gap-2 px-3.5 py-2 rounded-full bg-white/10 border border-white/15 text-white/95 backdrop-blur-sm shadow-sm">
              <CheckCircleIcon className="w-4 h-4 text-emerald-400" />
              <span>Setup in 2 minutes</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Video Demo Modal */}
      <AnimatePresence>
        {showVideoModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={closeVideoModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">StyleSync Demo</h3>
                  <p className="text-sm text-gray-600">See how creative teams collaborate in real-time</p>
                </div>
                <button
                  onClick={closeVideoModal}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <XMarkIcon className="w-6 h-6 text-gray-500" />
                </button>
              </div>

              {/* Video Content */}
              <div className="relative">
                {/* Placeholder Video Area */}
                <div className="relative bg-gradient-to-br from-blue-600 to-purple-600 aspect-video flex items-center justify-center">
                  <div className="text-center text-white">
                    <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <PlayIcon className="w-10 h-10 text-white" />
                    </div>
                    <h4 className="text-2xl font-bold mb-2">StyleSync Demo Video</h4>
                    <p className="text-blue-100 max-w-md mx-auto">
                      Watch how teams use StyleSync for brainstorming, mood boards, and design sprints
                    </p>
                  </div>
                  
                  {/* Demo Overlay Elements */}
                  <div className="absolute inset-0 pointer-events-none">
                    {/* Floating UI Elements */}
                    <motion.div
                      animate={{ y: [0, -10, 0] }}
                      transition={{ duration: 3, repeat: Infinity }}
                      className="absolute top-1/4 left-1/4 bg-white/90 rounded-lg p-3 shadow-lg"
                    >
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span className="text-xs font-medium text-gray-700">Sarah is online</span>
                      </div>
                    </motion.div>
                    
                    <motion.div
                      animate={{ y: [0, 10, 0] }}
                      transition={{ duration: 4, repeat: Infinity, delay: 1 }}
                      className="absolute top-1/3 right-1/4 bg-white/90 rounded-lg p-3 shadow-lg"
                    >
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        <span className="text-xs font-medium text-gray-700">Marcus added a card</span>
                      </div>
                    </motion.div>
                    
                    <motion.div
                      animate={{ x: [0, 20, 0] }}
                      transition={{ duration: 5, repeat: Infinity, delay: 2 }}
                      className="absolute bottom-1/4 left-1/3 bg-white/90 rounded-lg p-3 shadow-lg"
                    >
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                        <span className="text-xs font-medium text-gray-700">Real-time sync</span>
                      </div>
                    </motion.div>
                  </div>
                </div>

                {/* Video Controls */}
                <div className="p-6 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <button className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                        <PlayIcon className="w-4 h-4" />
                        <span>Play Demo</span>
                      </button>
                      <span className="text-sm text-gray-600">2:45 / 5:30</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button className="p-2 hover:bg-gray-200 rounded-lg transition-colors">
                        <span className="text-sm">🔊</span>
                      </button>
                      <button className="p-2 hover:bg-gray-200 rounded-lg transition-colors">
                        <span className="text-sm">⏯️</span>
                      </button>
                      <button className="p-2 hover:bg-gray-200 rounded-lg transition-colors">
                        <span className="text-sm">⏭️</span>
                      </button>
                    </div>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="mt-4">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <motion.div
                        className="bg-gradient-to-r from-blue-600 to-purple-600 h-2 rounded-full"
                        initial={{ width: "0%" }}
                        animate={{ width: "53%" }}
                        transition={{ duration: 2, delay: 1 }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Demo Features List */}
              <div className="p-6 border-t border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-3">What you'll see in this demo:</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center space-x-2">
                    <CheckCircleIcon className="w-4 h-4 text-green-500" />
                    <span>Real-time collaboration with live cursors</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircleIcon className="w-4 h-4 text-green-500" />
                    <span>Creating mood boards and brainstorming</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircleIcon className="w-4 h-4 text-green-500" />
                    <span>Kanban board organization</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircleIcon className="w-4 h-4 text-green-500" />
                    <span>Design sprint workflow</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* How It Works Section */}
      <section className="py-20 bg-white/80 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">How StyleSync Works</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Get started in minutes, not hours. Our intuitive platform guides you through every step.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {howItWorks.map((step, idx) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1, duration: 0.6 }}
                className="relative h-full lift-hover gradient-border"
              >
                <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow h-full flex flex-col">
                  <div className="absolute -top-4 -left-4 w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                    {step.step}
                  </div>
                  <div className="mt-4 flex-1 flex flex-col">
                    <step.icon className="w-12 h-12 text-blue-600 mb-4 flex-shrink-0" />
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">{step.title}</h3>
                    <p className="text-gray-600 flex-1">{step.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section - Canvas Style */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Powerful Features for Creative Teams</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need to bring your ideas to life, from initial concept to final presentation.
            </p>
          </motion.div>

          {/* Brainstorming - Content Left, Image Right */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-blue-100 text-blue-800 text-sm font-medium">
                <LightBulbIcon className="w-4 h-4 mr-2" />
                Brainstorming
              </div>
              <h3 className="text-3xl font-bold text-gray-900">Capture Ideas Instantly</h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                Transform your brainstorming sessions with real-time collaboration. Watch ideas flow as team members contribute simultaneously with live cursors and instant updates. No more waiting for turn-taking - everyone can contribute at once.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3" />
                  <span>Real-time idea sharing with live cursors</span>
                </li>
                <li className="flex items-center">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3" />
                  <span>Instant visual feedback and updates</span>
                </li>
                <li className="flex items-center">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3" />
                  <span>Organize thoughts with drag-and-drop ease</span>
                </li>
              </ul>
              <button
                onClick={() => navigate('/auth')}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                Start Brainstorming
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
                <div className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl p-8 shadow-2xl">
                <div className="bg-white/10 rounded-lg p-6 backdrop-blur-sm">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                        <UsersIcon className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-white font-medium">Team Session</span>
                    </div>
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                      <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                      <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse"></div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3 p-3 bg-white/10 rounded-lg">
                      <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                      <span className="text-white text-sm">
                        <TypingIndicator name="Sarah" />
                      </span>
                    </div>
                    <div className="flex items-center space-x-3 p-3 bg-white/10 rounded-lg">
                      <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                      <span className="text-white text-sm">Marcus added a new idea</span>
                    </div>
                    <div className="flex items-center space-x-3 p-3 bg-white/10 rounded-lg">
                      <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                      <span className="text-white text-sm">Emily connected two concepts</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Mood Board - Content Right, Image Left */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <MoodBoardDemo />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="space-y-6"
            >
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-pink-100 text-pink-800 text-sm font-medium">
                <SparklesIcon className="w-4 h-4 mr-2" />
                Mood Boards
              </div>
              <h3 className="text-3xl font-bold text-gray-900">Visualize Your Vision</h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                Create stunning mood boards that capture the essence of your projects. Upload images, add text, and organize visual elements to communicate your creative vision clearly to your team and clients.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3" />
                  <span>Drag-and-drop image organization</span>
                </li>
                <li className="flex items-center">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3" />
                  <span>Real-time collaborative editing</span>
                </li>
                <li className="flex items-center">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3" />
                  <span>Professional presentation templates</span>
                </li>
              </ul>
              <button
                onClick={() => navigate('/auth')}
                className="px-6 py-3 bg-pink-600 text-white rounded-lg font-semibold hover:bg-pink-700 transition-colors"
              >
                Create Mood Board
              </button>
            </motion.div>
          </div>

          {/* Kanban Board - Content Left, Image Right */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-green-100 text-green-800 text-sm font-medium">
                <Squares2X2Icon className="w-4 h-4 mr-2" />
                Kanban Boards
              </div>
              <h3 className="text-3xl font-bold text-gray-900">Organize Your Workflow</h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                Streamline your project management with intuitive Kanban boards. Move tasks through stages, assign team members, and track progress in real-time. Keep everyone on the same page with visual project management.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3" />
                  <span>Drag-and-drop task management</span>
                </li>
                <li className="flex items-center">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3" />
                  <span>Real-time progress tracking</span>
                </li>
                <li className="flex items-center">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3" />
                  <span>Team assignment and notifications</span>
                </li>
              </ul>
              <button
                onClick={() => navigate('/auth')}
                className="px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors"
              >
                Build Kanban Board
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <KanbanBoardDemo />
            </motion.div>
          </div>

          {/* Design Sprint - Content Right, Image Left */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <DesignSprintDemo />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="space-y-6"
            >
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-purple-100 text-purple-800 text-sm font-medium">
                <RocketLaunchIcon className="w-4 h-4 mr-2" />
                Design Sprints
              </div>
              <h3 className="text-3xl font-bold text-gray-900">Accelerate Innovation</h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                Run structured design sprints to solve complex problems in just 5 days. Follow proven methodologies with built-in templates and tools that guide your team through each phase of the sprint process.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3" />
                  <span>5-day structured methodology</span>
                </li>
                <li className="flex items-center">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3" />
                  <span>Built-in templates and tools</span>
                </li>
                <li className="flex items-center">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3" />
                  <span>Team collaboration at every stage</span>
                </li>
              </ul>
              <button
                onClick={() => navigate('/auth')}
                className="px-6 py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors"
              >
                Start Design Sprint
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Loved by Creative Teams Worldwide</h2>
            <p className="text-xl text-gray-600">See what our users are saying about StyleSync</p>
          </motion.div>

          <div className="relative">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTestimonial}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.5 }}
                className="text-center"
              >
                <div className="max-w-3xl mx-auto">
                  <img
                    src={testimonials[activeTestimonial].avatarUrl}
                    alt={`${testimonials[activeTestimonial].name} headshot`}
                    className="w-24 h-24 rounded-full mx-auto mb-6 object-cover shadow-lg border-4 border-white"
                    loading="lazy"
                  />
                  <blockquote className="text-2xl text-gray-700 mb-6 italic leading-relaxed">
                    "{testimonials[activeTestimonial].content}"
                  </blockquote>
                  <div className="flex justify-center space-x-1 mb-4">
                    {[...Array(testimonials[activeTestimonial].rating)].map((_, i) => (
                      <StarIcon key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{testimonials[activeTestimonial].name}</p>
                    <p className="text-gray-600">{testimonials[activeTestimonial].role}</p>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            <div className="flex justify-center space-x-2 mt-8">
              {testimonials.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveTestimonial(idx)}
                  className={`w-3 h-3 rounded-full transition-colors ${
                    activeTestimonial === idx ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Simple, Transparent Pricing</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Choose the plan that fits your team's needs. Start free, upgrade when you're ready.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {pricingPlans.map((plan, idx) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1, duration: 0.6 }}
                className={`lift-hover relative bg-white rounded-2xl shadow-lg border-2 p-8 h-full flex flex-col ${
                  plan.popular ? 'border-blue-500 shadow-xl' : 'border-gray-200'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                  <div className="mb-4">
                    <span className="text-4xl font-bold text-gray-900">{plan.price}</span>
                    <span className="text-gray-600">/{plan.period}</span>
                  </div>
                  <p className="text-gray-600">{plan.description}</p>
                </div>

                <ul className="space-y-4 mb-8 flex-1">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-center">
                      <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                 <button
                   onClick={() => navigate('/auth')}
                   className={`w-full py-3 px-6 rounded-lg font-semibold transition-all duration-300 mt-auto ${
                    plan.buttonVariant === 'primary'
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:shadow-lg transform hover:scale-105'
                      : 'border-2 border-gray-300 text-gray-700 hover:border-blue-500 hover:text-blue-600'
                  }`}
                 >
                   {plan.buttonText}
                 </button>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mt-12"
          >
            <p className="text-gray-600 mb-4">Free plan stays free. Upgrade to Pro anytime.</p>
            <p className="text-sm text-gray-500">
              Need a custom plan? <a href="/contact" className="text-blue-600 hover:underline">Contact our sales team</a>
            </p>
          </motion.div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-4xl mx-auto text-center px-6">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-4xl sm:text-5xl font-bold mb-6"
          >
            Ready to Transform Your Creative Process?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl mb-8 text-blue-100"
          >
            Join thousands of creative teams who've already discovered the power of StyleSync
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <button
              onClick={() => navigate('/auth')}
              className="px-8 py-4 rounded-xl bg-white text-blue-600 font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
            >
              Start Your Free Trial
              <ArrowRightIcon className="w-5 h-5 ml-2 inline" />
            </button>
            <button
              onClick={() => navigate('/contact')}
              className="px-8 py-4 rounded-xl border-2 border-white text-white font-semibold text-lg hover:bg-white hover:text-blue-600 transition-all duration-300"
            >
              Schedule a Demo
            </button>
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mt-6 text-blue-100 text-sm"
          >
            No credit card required • Free forever plan • Setup in 2 minutes
          </motion.p>
        </div>
      </section>

      {/* Footer */}
      <AppFooter />
    </div>
  );
};

export default LandingPage;
